import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import { sendEmail } from '../../services/api';

export default function Schedule() {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', service: '' });
  const [loading, setLoading] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);

  // Generate dates for the next 7 business days starting from today
  const generateWeekDates = () => {
    const today = new Date();
    const dates = [];
    let currentDate = new Date(today);
    
    while (dates.length < 5) { // Get 5 business days
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        dates.push(new Date(currentDate));
      }
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Generate time slots from 9 AM to 5 PM
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 17; hour++) {
      const time12 = hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`;
      const time24 = `${hour.toString().padStart(2, '0')}:00`;
      slots.push({ display: time12, value: time24 });
    }
    return slots;
  };

  const weekDates = generateWeekDates();
  const timeSlots = generateTimeSlots();

  // Set default booked slots after page load
  useEffect(() => {
    // Example: Mark some slots as booked
    // In a real application, this data would come from a backend service
    //const defaultBookedSlots = ['10:00', '14:00'];
    //i want to map the booked slots to include date and time
    const defaultBookedSlots = [
        { date: weekDates[0], time: '09:00' },{ date: weekDates[4], time: '10:00' },{ date: weekDates[0], time: '15:00' },
        { date: weekDates[4], time: '09:00' },{ date: weekDates[1], time: '10:00' },{ date: weekDates[1], time: '15:00' },
        { date: weekDates[2], time: '10:00' }, { date: weekDates[4], time: '14:00' }];
    setBookedSlots(defaultBookedSlots);
  }, []);

  // Check if a slot is booked
  const isSlotBooked = (date, timeValue) => {
    return bookedSlots.some(slot => 
      slot.date.toDateString() === date.toDateString() && slot.time === timeValue
    );
  };


  const handleSlotClick = (date, time) => {
    setSelectedSlot({ date, time });
    setShowModal(true);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await sendEmail({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: 
          `<strong>Consultation Booking Request</strong><br>` +
          `<strong>Name:</strong> ${form.name}<br>` +
          `<strong>Phone:</strong> ${form.phone}<br>` +
          `<strong>Email:</strong> ${form.email}<br>` +
           `<strong>Service of Interest:</strong> ${form.service}<br>` +
          `<strong>Selected Date:</strong> ${selectedSlot.date.toLocaleDateString()}<br>` +
          `<strong>Selected Time:</strong> ${selectedSlot.time.display}<br>` +
          `<strong>Message:</strong> ${form.message}`
      });
      
      if (response.status === 200) {
        alert('Your consultation request has been sent successfully!');
        setForm({ name: '', email: '', phone: '', message: '', service: '' });
        setShowModal(false);
        setSelectedSlot(null);
      } else {
        alert('Failed to send request. Please try again later.');
      }
    } catch (error) {
      alert('An error occurred. Please try again later.');
    }
    setLoading(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSlot(null);
    setForm({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 lg:pt-18">
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden hero-bg">
          {/* Background Elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float"></div>
            <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-accent/20 rounded-full blur-3xl animate-float animation-delay-400"></div>
          </div>

          <div className="relative w-full max-w-6xl mx-auto container-padding py-20">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
              <h1 className="text-4xl md:text-5xl font-bold text-center text-white mb-8">
                Schedule a <span className="text-gradient">Consultation</span>
              </h1>
              
              {/* Calendar Grid */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-4 text-white font-semibold border-b border-white/20">Time</th>
                      {weekDates.map((date, index) => (
                        <th key={index} className="p-4 text-white font-semibold border-b border-white/20 min-w-32">
                          <div>{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                          <div className="text-sm text-secondary-200">{date.getDate()}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((time, timeIndex) => (
                      <tr key={timeIndex}>
                        <td className="p-2 text-white font-medium border-r border-white/20">
                          {time.display}
                        </td>
                        {weekDates.map((date, dateIndex) => {
                          const isBooked = isSlotBooked(date, time.value);
                          return (
                            <td key={dateIndex} className="p-2 border-r border-white/20">
                              <button
                                disabled={isBooked}
                                onClick={() => !isBooked && handleSlotClick(date, time)}
                                className={`w-full h-12 border border-white/20 rounded-lg transition-colors duration-200 text-white text-sm ${
                                  isBooked 
                                    ? 'bg-red-500/30 cursor-not-allowed opacity-60' 
                                    : 'bg-white/5 hover:bg-accent/30 hover:scale-105'
                                }`}
                              >
                                {isBooked ? 'Booked' : 'Available'}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8 w-full max-w-md">
            <h2 className="text-2xl font-bold text-white mb-6">Book Consultation</h2>
            
            {selectedSlot && (
              <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/20">
                <p className="text-white font-semibold">Selected Time:</p>
                <p className="text-secondary-200">
                  {selectedSlot.date.toLocaleDateString()} at {selectedSlot.time.display}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-white font-semibold mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-accent"
                  required
                  disabled={loading}
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-white font-semibold mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-accent"
                  required
                  disabled={loading}
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-white font-semibold mb-2">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-accent"
                  required
                  disabled={loading}
                />
              </div>
              <div>
                    <label htmlFor="service" className="block text-white font-semibold mb-2">Service of Interest</label>
                    <select
                      id="service"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-accent"
                    >
                      <option value="">Select a service</option>
                      <option value="Consulting">Consulting</option>
                      <option value="Business Strategy">Business Strategy</option>
                      <option value="Landscape">Landscape</option>
                      <option value="Development">Development</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Support">Support</option>
                    </select>
                  </div>
              
              <div>
                <label htmlFor="message" className="block text-white font-semibold mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows="3"
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                  placeholder="Tell us about your consultation needs..."
                />
              </div>

              <div>                    
                    <label htmlFor="consent" className="block text-white font-semibold mb-2 text-sm">
                      I consent to YLS collecting and storing my data from this form to process my request.
                    </label>
                    <input type="checkbox" id="consent" name="consent" className="mr-2" required/>
                  </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-3 bg-white/10 text-white rounded-lg border border-white/20 hover:bg-white/20 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`flex-1 btn-accent py-3 rounded-lg font-bold ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? 'Booking...' : 'Book Consultation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}